import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { getCheckpointResponse, getEnrolledCourses, getHomeworkOverrides, getMyHomework, type HomeworkSubmission } from "../lib/firebase";
import { AMF_MODULES, HOMEWORK, type Homework } from "../lib/homework";
import { HomeworkCard } from "../components/HomeworkCard";
import { CHECKPOINT_SETS } from "../lib/checkpoints";
import { CheckpointCard } from "../components/CheckpointCard";

/** AI Money Factory: every module's quiz + homework (Dew, 2026-10-19). Modules sit in a list on the
 *  left like folders; the student opens one at a time (Dew, 2026-10-20). ?m=<n> picks the module.
 *  Each quiz answered or homework sent = 25 leaderboard points (counted on the server). */
export function HomeworkPage() {
  const { user, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [owned, setOwned] = useState<Set<string> | null>(null);
  const [sent, setSent] = useState<Map<string, HomeworkSubmission>>(new Map());
  const [quizDone, setQuizDone] = useState<Set<number>>(new Set());
  // Admin-edited homework (homeworkTasks/{id}) wins over the defaults in lib/homework.ts.
  const [tasks, setTasks] = useState<Map<string, Homework>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([getEnrolledCourses(user.uid), getMyHomework(user.uid)])
      .then(([courses, hw]) => {
        setOwned(new Set(courses.map((c) => c.id)));
        setSent(hw);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // Sidebar ticks only: a module's quiz counts as done once any answer is saved.
    Promise.all(AMF_MODULES.map((m) =>
      getCheckpointResponse(user.uid, m.courseId, `amf-quiz-m${m.n}`).then((r) => (r && !r.skipped ? m.n : 0)).catch(() => 0)))
      .then((ns) => setQuizDone(new Set(ns.filter(Boolean))));
    getHomeworkOverrides().then(setTasks).catch(() => {});
  }, [user]);

  if (!user) return null;
  if (error) return <main><p className="error">{error}</p></main>;
  if (!owned) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;

  const can = (courseId: string) => isAdmin || owned.has(courseId);
  // Open the module asked for, else the first one the student owns.
  const picked = Number(params.get("m")) || AMF_MODULES.find((m) => can(m.courseId))?.n || 1;
  const m = AMF_MODULES.find((x) => x.n === picked) ?? AMF_MODULES[0];
  const base = HOMEWORK.find((h) => h.module === m.n);
  const hw = base && (tasks.get(base.id) ?? base);
  const quizId = `amf-quiz-m${m.n}`;

  return (
    <div className="hw-shell">
      <aside className="hw-side">
        <Link to="/dashboard?group=AI%20Money%20Factory" className="course-sidebar__back">← AI Money Factory</Link>
        <h2 className="hw-side__title">การบ้าน + Quiz</h2>
        <p className="hw-side__sub">ทำ Quiz หรือส่งการบ้าน ได้ชิ้นละ 25 แต้ม</p>
        <nav>
          {AMF_MODULES.map((x) => {
            const h = HOMEWORK.find((y) => y.module === x.n);
            return (
              <button key={x.n} type="button" disabled={!can(x.courseId)}
                className={`hw-side__item${x.n === m.n ? " is-on" : ""}`}
                onClick={() => setParams({ m: String(x.n) })}>
                <span className="hw-side__name"><b>Module {x.n}</b> {x.title}</span>
                <span className="hw-side__ticks">
                  {can(x.courseId) ? (
                    <>
                      {CHECKPOINT_SETS[`amf-quiz-m${x.n}`] && <span className={quizDone.has(x.n) ? "is-done" : ""}>{quizDone.has(x.n) ? "✓" : "○"} Quiz</span>}
                      {h && <span className={sent.has(h.id) ? "is-done" : ""}>{sent.has(h.id) ? "✓" : "○"} การบ้าน</span>}
                    </>
                  ) : <span>ยังไม่มีแพ็กนี้</span>}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <main className="hw-main">
        <span className="kicker">Module {m.n}</span>
        <h1 className="hw-main__title">{m.title}</h1>
        {!can(m.courseId) ? (
          <p className="hw-locked">Module นี้อยู่ในแพ็กที่คุณยังไม่มี</p>
        ) : (
          <>
            {CHECKPOINT_SETS[quizId] && (
              <CheckpointCard key={quizId} userId={user.uid} courseId={m.courseId} setId={quizId} variant="lesson" />
            )}
            {hw && <HomeworkCard key={hw.id} userId={user.uid} courseId={m.courseId} hw={hw} sent={sent.get(hw.id)} isAdmin={isAdmin}
              onSent={(s) => setSent(new Map(sent).set(hw.id, s))}
              onEdited={(h) => setTasks(new Map(tasks).set(h.id, h))} />}
          </>
        )}
      </main>
    </div>
  );
}
